/**
 * ComparisonMetricCharts
 *
 * Dual-line metric charts below the GapChart on the Race Dynamics tab.
 * Each chart shows A (blue) and B (amber) for a single metric.
 * Charts are toggle-able via chips.
 */

import { useState, useCallback, useMemo } from 'react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
} from 'recharts';
import { Box, Typography, Chip, useTheme, useMediaQuery } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { tokens } from '../../../theme';

const COLOR_A = '#3b82f6';
const COLOR_B = '#f59e0b';

const METRICS = [
  { key: 'pace', i18nKey: 'paceMetric', label: 'Pace', keyA: 'pace_a', keyB: 'pace_b', unit: '/km', formatter: formatPace, invert: true },
  { key: 'gap', i18nKey: 'normPaceLabel', label: 'Norm. Pace', keyA: 'gap_a', keyB: 'gap_b', unit: '/km', formatter: formatPace, invert: true },
  { key: 'hr', i18nKey: 'heartRate', label: 'Heart Rate', keyA: 'hr_a', keyB: 'hr_b', unit: 'bpm', formatter: (v) => v?.toFixed(0) ?? '—' },
  { key: 'cadence', i18nKey: 'cadence', label: 'Cadence', keyA: 'cadence_a', keyB: 'cadence_b', unit: 'spm', formatter: (v) => v?.toFixed(0) ?? '—' },
  { key: 'effort', i18nKey: 'effortLabel', label: 'Effort', keyA: 'effort_a', keyB: 'effort_b', unit: '', formatter: (v) => v?.toFixed(1) ?? '—' },
  { key: 'asc_vspeed', i18nKey: 'ascVSpeedLabel', label: 'Asc V.Speed', keyA: 'asc_vertical_speed_a', keyB: 'asc_vertical_speed_b', unit: 'm/h', formatter: (v) => v != null ? (v * 3600).toFixed(0) : '—' },
  { key: 'desc_vspeed', i18nKey: 'descVSpeedLabel', label: 'Desc V.Speed', keyA: 'desc_vertical_speed_a', keyB: 'desc_vertical_speed_b', unit: 'm/h', formatter: (v) => v != null ? (v * 3600).toFixed(0) : '—' },
];

function formatPace(minPerKm) {
  if (minPerKm == null) return '—';
  const m = Math.floor(minPerKm);
  const s = Math.round((minPerKm - m) * 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function MetricTooltip({ active, payload, label, metric }) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload;
  if (!row) return null;
  const km = (row.course_distance_m / 1000).toFixed(1);
  const valA = metric.formatter(row[metric.keyA]);
  const valB = metric.formatter(row[metric.keyB]);

  return (
    <Box sx={{ backgroundColor: colors.grey[800], border: `1px solid ${colors.grey[700]}`, borderRadius: 1, p: 1, minWidth: 140 }}>
      <Typography variant="caption" fontWeight="bold" color={colors.grey[200]} display="block">km {km}</Typography>
      <Typography variant="caption" color={COLOR_A} display="block">A: {valA} {metric.unit}</Typography>
      <Typography variant="caption" color={COLOR_B} display="block">B: {valB} {metric.unit}</Typography>
    </Box>
  );
}

function SingleMetricChart({ resampled, metric, hoverDistanceM, onHover, zoomDomain }) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation();

  const handleMouseMove = useCallback(
    (e) => { if (e?.activePayload?.[0]) onHover?.(e.activePayload[0].payload.course_distance_m); },
    [onHover]
  );

  // Filter out null rows for this metric
  const data = useMemo(
    () => resampled.filter((r) => r[metric.keyA] != null || r[metric.keyB] != null),
    [resampled, metric]
  );

  // X-axis domain (same logic as GapChart for alignment)
  const xDomain = useMemo(() => {
    if (zoomDomain) return zoomDomain;
    if (!resampled.length) return [0, 1000];
    return [0, resampled[resampled.length - 1].course_distance_m];
  }, [resampled, zoomDomain]);

  if (!data.length) return null;

  return (
    <Box mb={2}>
      <Typography variant="subtitle2" color={colors.grey[300]} mb={0.5}>
        {t(metric.i18nKey, metric.label)}
      </Typography>
      <ResponsiveContainer width="100%" height={160}>
        <LineChart data={data} margin={isMobile ? { top: 4, right: 5, left: 0, bottom: 4 } : { top: 4, right: 60, left: 10, bottom: 4 }} onMouseMove={handleMouseMove} onMouseLeave={() => onHover?.(null)}>
          <CartesianGrid strokeDasharray="3 3" stroke={colors.grey[700]} />
          <XAxis
            dataKey="course_distance_m"
            tickFormatter={(v) => `${(v / 1000).toFixed(0)}`}
            domain={xDomain}
            type="number"
            tick={{ fill: colors.grey[500], fontSize: 10 }}
          />
          <YAxis
            tick={isMobile ? false : { fill: colors.grey[500], fontSize: 10 }}
            axisLine={!isMobile}
            tickLine={!isMobile}
            tickFormatter={metric.formatter}
            width={isMobile ? 0 : 55}
            reversed={metric.invert}
          />
          {/* Hidden right Y-axis to match Gap Chart's elevation axis spacing */}
          <YAxis
            yAxisId="hidden"
            orientation="right"
            width={isMobile ? 0 : 45}
            tick={false}
            axisLine={false}
            tickLine={false}
          />
          <Line dataKey={metric.keyA} stroke={COLOR_A} strokeWidth={1.5} dot={false} isAnimationActive={false} />
          <Line dataKey={metric.keyB} stroke={COLOR_B} strokeWidth={1.5} dot={false} isAnimationActive={false} />
          {hoverDistanceM != null && (
            <ReferenceLine x={hoverDistanceM} stroke={colors.grey[400]} strokeWidth={1} />
          )}
          <Tooltip content={<MetricTooltip metric={metric} />} />
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
}

export default function ComparisonMetricCharts({ resampled = [], hoverDistanceM, onHover, zoomDomain, onZoomChange }) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { t } = useTranslation();
  const [active, setActive] = useState(new Set(['pace', 'hr']));

  const toggle = (key) =>
    setActive((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  // Filter by zoom domain
  const data = useMemo(() => {
    if (!zoomDomain) return resampled;
    return resampled.filter(
      (r) => r.course_distance_m >= zoomDomain[0] && r.course_distance_m <= zoomDomain[1]
    );
  }, [resampled, zoomDomain]);

  return (
    <Box>
      {/* Toggle chips */}
      <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
        {METRICS.map((m) => (
          <Chip
            key={m.key}
            label={t(m.i18nKey, m.label)}
            size="small"
            onClick={() => toggle(m.key)}
            variant={active.has(m.key) ? 'filled' : 'outlined'}
            sx={{
              backgroundColor: active.has(m.key) ? colors.blueAccent[800] : 'transparent',
              color: active.has(m.key) ? colors.grey[100] : colors.grey[400],
              borderColor: colors.grey[600],
              cursor: 'pointer',
            }}
          />
        ))}
      </Box>

      {METRICS.filter((m) => active.has(m.key)).map((metric) => (
        <SingleMetricChart
          key={metric.key}
          resampled={data}
          metric={metric}
          hoverDistanceM={hoverDistanceM}
          onHover={onHover}
          zoomDomain={zoomDomain}
        />
      ))}

      {/* Legend */}
      <Box display="flex" gap={3} mt={1}>
        <Box display="flex" alignItems="center" gap={0.5}>
          <Box sx={{ width: 20, height: 2, backgroundColor: COLOR_A }} />
          <Typography variant="caption" color={colors.grey[400]}>A</Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={0.5}>
          <Box sx={{ width: 20, height: 2, backgroundColor: COLOR_B }} />
          <Typography variant="caption" color={colors.grey[400]}>B</Typography>
        </Box>
      </Box>
    </Box>
  );
}
